import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { X, Plus, CalendarIcon, Pin, Share2, Save, CheckSquare } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { NoteChecklistManager } from './NoteChecklistManager';

type Note = {
  id: string;
  title: string;
  content: any;
  category: 'general' | 'pinned' | 'ideas' | 'reminders';
  color: string;
  visibility: 'private' | 'board_shared';
  is_pinned: boolean;
  reminder_date?: string;
  reminder_sent: boolean;
  position: number;
  tags: string[];
  created_at: string;
  updated_at: string;
  board_id?: string;
  user_id: string;
};

interface NoteEditorProps {
  note: Note;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNoteSaved: () => void;
}

const NOTE_COLORS = [
  '#ffffff', // White
  '#fef3c7', // Yellow
  '#fecaca', // Red
  '#bbf7d0', // Green
  '#bfdbfe', // Blue
  '#e0e7ff', // Indigo
  '#f3e8ff', // Purple
  '#fce7f3', // Pink
];

const NOTE_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'ideas', label: 'Ideas' },
  { value: 'reminders', label: 'Reminders' },
];

export const NoteEditor = ({ note, open, onOpenChange, onNoteSaved }: NoteEditorProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'general' | 'ideas' | 'reminders'>(
    note.category === 'pinned' ? 'general' : note.category
  );
  const [color, setColor] = useState(note.color);
  const [selectedBoardId, setSelectedBoardId] = useState<string>(note.board_id || 'private');
  const [boards, setBoards] = useState<{ id: string; title: string }[]>([]);
  const [isPinned, setIsPinned] = useState(note.is_pinned);
  const [reminderDate, setReminderDate] = useState<Date | undefined>(
    note.reminder_date ? new Date(note.reminder_date) : undefined
  );
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(note.tags);
  const [hasChecklists, setHasChecklists] = useState(false);

  // Fetch user's accessible boards
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const { data, error } = await supabase
          .from('boards')
          .select('id, title')
          .order('title');

        if (error) throw error;
        setBoards(data || []);
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    };

    if (open) {
      fetchBoards();
    }
  }, [open]);

  // Extract content text from structured content
  useEffect(() => {
    if (note.content) {
      if (typeof note.content === 'string') {
        setContent(note.content);
      } else if (note.content.type === 'doc' && note.content.content) {
        const extractText = (nodes: any[]): string => {
          let text = '';
          for (const node of nodes) {
            if (node.type === 'text') {
              text += node.text || '';
            } else if (node.content) {
              text += extractText(node.content);
            }
          }
          return text;
        };
        setContent(extractText(note.content.content));
      }
    }
  }, [note.content]);

  const handleAddTag = () => {
    const newTag = tagInput.trim().toLowerCase();
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Title is required",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const updateData: any = {
        title: title.trim(),
        content: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: content }] }] },
        category,
        color,
        visibility: selectedBoardId !== 'private' ? 'board_shared' : 'private',
        board_id: selectedBoardId !== 'private' ? selectedBoardId : null,
        is_pinned: isPinned,
        tags,
        reminder_date: reminderDate?.toISOString() || null,
      };

      const { error } = await supabase
        .from('reminder_notes')
        .update(updateData)
        .eq('id', note.id);

      if (error) throw error;

      // Log activity
      await supabase
        .from('note_activities')
        .insert({
          note_id: note.id,
          user_id: note.user_id,
          activity_type: 'updated',
          details: { 
            changes: ['content', 'title', 'category', 'color', 'visibility', 'tags'] 
          }
        });

      toast({
        title: "Success",
        description: "Note updated successfully",
      });

      onNoteSaved();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating note:', error);
      toast({
        title: "Error",
        description: "Failed to update note",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            Edit Note
            {hasChecklists && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <CheckSquare className="h-3 w-3" />
                Has Checklists
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter note title..."
              required
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Content</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note content..."
              rows={8}
            />
          </div>

          {/* Checklists Section */}
          <div className="space-y-2">
            <Separator />
            <NoteChecklistManager 
              noteId={note.id}
              noteVisibility={selectedBoardId !== 'private' ? 'board_shared' : 'private'}
              noteBoardId={selectedBoardId !== 'private' ? selectedBoardId : null}
              onChecklistChange={setHasChecklists}
            />
            <Separator />
          </div>

          {/* Settings Row 1 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select value={category} onValueChange={(value: any) => setCategory(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Share to Board</label>
              <Select value={selectedBoardId} onValueChange={setSelectedBoardId}>
                <SelectTrigger>
                  <SelectValue placeholder="Private note" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private note</SelectItem>
                  {boards.map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      {board.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Settings Row 2 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch
                id="pinned"
                checked={isPinned}
                onCheckedChange={setIsPinned}
              />
              <Label htmlFor="pinned" className="flex items-center gap-2">
                <Pin className="h-4 w-4" />
                Pin note
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Reminder:</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "justify-start text-left font-normal",
                      !reminderDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {reminderDate ? format(reminderDate, "PPP") : "Set reminder"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={reminderDate}
                    onSelect={setReminderDate}
                    initialFocus
                  />
                  {reminderDate && (
                    <div className="p-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setReminderDate(undefined)}
                      >
                        Clear reminder
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Color</label>
            <div className="flex gap-2 flex-wrap">
              {NOTE_COLORS.map((noteColor) => (
                <button
                  key={noteColor}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === noteColor ? 'border-foreground scale-110' : 'border-border hover:scale-105'
                  }`}
                  style={{ backgroundColor: noteColor }}
                  onClick={() => setColor(noteColor)}
                />
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Tags</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={handleAddTag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !title.trim()}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
